import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getUser } from "@/lib/secureStorage";
import { ScissorsIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const n = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const today = () => new Date().toLocaleDateString("en-CA");
const errText = (e) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === "object" ? d.message || d.code : d) || "Short Close failed";
};

// Merge MI issued / reversal-returned with production consumption per (code, uom).
const cwoMaterial = (c) => {
  const m = {};
  const row = (l) => (m[`${l.code}|${l.uom}`] ||= { code: l.code, uom: l.uom, issued: 0, returned: 0, consumed: 0 });
  (c.material?.lines || []).forEach(l => { const r = row(l); r.issued += l.issued || 0; r.returned += l.returned_by_reversal || 0; });
  (c.production?.lines || []).forEach(l => { if (l.consumed) row(l).consumed += l.consumed; });
  return Object.values(m).map(r => ({ ...r, toReturn: Math.max(0, r.issued - r.returned - r.consumed) }));
};

const Stat = ({ label, value, tid }) => (
  <div className="border border-[var(--border)] rounded-sm px-3 py-2">
    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
    <div data-testid={tid} className="font-mono tabular-nums text-lg text-white">{value}</div>
  </div>
);

const CwoMaterialTable = ({ c }) => {
  const rows = cwoMaterial(c);
  return (
    <div data-testid={`wo-sc-cwo-${c.cwo_no}`} className="border border-[var(--border)] rounded-sm">
      <div className="px-3 py-2 text-xs font-mono text-zinc-300 border-b border-[var(--border)]">
        {c.cwo_no} · {c.stage_code || "—"} · {c.status}
      </div>
      {rows.length === 0 ? <div className="px-3 py-2 text-xs text-zinc-500">No material issued</div> : (
        <table className="w-full text-xs table-fixed">
          <thead className="text-zinc-500 font-mono uppercase text-[10px]"><tr>
            <th className="text-left px-3 py-1">Material</th><th className="text-right px-2 w-24">Issued</th>
            <th className="text-right px-2 w-24">Consumed</th><th className="text-right px-2 w-24">Reversed</th><th className="text-right px-3 w-24">To Return</th>
          </tr></thead>
          <tbody>{rows.map(r => (
            <tr key={`${r.code}|${r.uom}`} className="border-t border-[var(--border)] font-mono tabular-nums">
              <td className="px-3 py-1 text-zinc-300">{r.code} <span className="text-zinc-500">{r.uom || "UOM?"}</span></td>
              <td className="text-right px-2">{n(r.issued)}</td><td className="text-right px-2">{n(r.consumed)}</td>
              <td className="text-right px-2">{n(r.returned)}</td>
              <td className={`text-right px-3 ${r.toReturn > 0 ? "text-amber-300" : "text-zinc-500"}`}>{n(r.toReturn)}</td>
            </tr>))}</tbody>
        </table>)}
    </div>
  );
};

// WO-SC-01 · Admin-only Short Close with read-only reconciliation preview. Hidden while on Hold.
export function ShortCloseButton({ doc, produced = 0, onDone }) {
  const [snap, setSnap] = useState(null);
  const [reason, setReason] = useState("");
  const [eff, setEff] = useState(today());
  const [busy, setBusy] = useState(false);
  if (!doc?.id || getUser()?.role !== "Administrator") return null;
  if (doc.on_hold === true || doc.status !== "Approved" || doc.short_close?.status === "active") return null;

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/production-orders/${doc.id}/reconciliation`);
      setReason(""); setEff(today()); setSnap(data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const ordered = snap?.ordered || 0;
  const balance = Math.max(0, ordered - produced);

  const submit = async () => {
    const r = reason.trim();
    if (r.length < 4) { toast.error("Reason is required (min 4 characters)"); return; }
    setBusy(true);
    try {
      await api.post(`/production-orders/${doc.id}/short-close`, {
        reason: r, effective_date: eff,
        reconciliation: { ordered, produced, balance, snapshot_hash: snap.snapshot_hash },
        reconciliation_ack: { snapshot_hash: snap.snapshot_hash },
      });
      toast.success(`${doc.po_no} · Short Closed`);
      setSnap(null); onDone?.();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" data-testid="wo-short-close-btn" onClick={open} disabled={busy}
        className="px-4 py-2 rounded-sm border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 text-sm font-semibold flex items-center gap-2">
        <ScissorsIcon size={14} weight="bold" /> Short Close
      </button>
      <Dialog open={!!snap} onOpenChange={(o) => !o && !busy && setSnap(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wo-short-close-dialog">
          <DialogHeader><DialogTitle className="font-display">Short Close · {doc.po_no}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Ordered" value={n(ordered)} tid="wo-sc-ordered" />
            <Stat label="Produced" value={n(produced)} tid="wo-sc-produced" />
            <Stat label="Balance to Close" value={n(balance)} tid="wo-sc-balance" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2">Child WO material reconciliation</div>
          <div className="space-y-2" data-testid="wo-sc-cwo-list">
            {(snap?.cwos || []).length === 0 ? <div className="text-xs text-zinc-500">No Child WOs</div>
              : snap.cwos.map(c => <CwoMaterialTable key={c.cwo_id} c={c} />)}
          </div>
          {snap?.material_reconciliation_incomplete && (
            <p data-testid="wo-sc-incomplete" className="text-xs text-amber-300">Some material sources are not yet included in this reconciliation.</p>
          )}
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Effective Date *</label>
              <input type="date" data-testid="wo-sc-date" value={eff} min={snap?.wo_date || undefined} onChange={e => setEff(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Reason *</label>
              <textarea data-testid="wo-sc-reason" value={reason} onChange={e => setReason(e.target.value)} rows={2}
                placeholder="Minimum 4 characters"
                className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setSnap(null)} disabled={busy}
              className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button type="button" data-testid="wo-sc-confirm" onClick={submit} disabled={busy || reason.trim().length < 4 || !eff}
              className="px-4 py-2 rounded-sm bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-700 disabled:cursor-not-allowed text-sm font-semibold text-white">
              {busy ? "Saving…" : "Confirm Short Close"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
